import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useDashboardChart } from "@/hooks/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useDashboardUrlState } from "./useDashboardUrlState";
import { FiltersCard } from "./filters-card";

const DASHBOARD_ID = "churn";

const BAR_COLOR = "#D0A84C";
const LINE_COLOR = "#6B8AFF";
const SIZE_COLOR = "var(--color-admin-text-subtle)";
const axisStyle = { fontSize: 11, fill: "rgba(244, 244, 245, 0.60)" };

const GENDERS = ["", "female", "male"] as const;
const GENDER_LABELS: Record<string, string> = {
  "": "Alle kjønn",
  female: "Kvinner",
  male: "Menn",
};

const AGE_BUCKETS = [
  "",
  "Under 25",
  "25–34",
  "35–44",
  "45–54",
  "55–64",
  "65+",
  "Ukjent",
] as const;

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().split("T")[0]!;
}

type HeadlineRow = {
  active_now: number | string;
  pending_cancel: number | string;
  reactivations_this_month: number | string;
};

type TrendRow = { day: string; active: number | string };

type TimeToChurnRow = {
  bucket: string;
  sort_order: number | string;
  count: number | string;
};

type RetentionRow = {
  signup_month: string;
  cohort_size: number | string;
  retained: number | string;
  churned: number | string;
  retention_pct: string;
  churn_pct: string;
};

type TierRow = { tier: string };

export function ChurnDashboard() {
  const [{ from, to, gender, ageBucket, tier }, update] = useDashboardUrlState({
    from: isoDaysAgo(730),
    to: isoDaysAgo(0),
    gender: "",
    ageBucket: "",
    tier: "",
  });

  const headlineParams = useMemo(
    () => ({ gender: gender || undefined, ageBucket: ageBucket || undefined }),
    [gender, ageBucket],
  );
  const trendParams = useMemo(
    () => ({
      from,
      to,
      gender: gender || undefined,
      ageBucket: ageBucket || undefined,
    }),
    [from, to, gender, ageBucket],
  );
  const histogramParams = useMemo(
    () => ({
      gender: gender || undefined,
      ageBucket: ageBucket || undefined,
      tier: tier || undefined,
    }),
    [gender, ageBucket, tier],
  );
  const retentionParams = useMemo(
    () => ({ gender: gender || undefined, ageBucket: ageBucket || undefined }),
    [gender, ageBucket],
  );

  const headline = useDashboardChart<typeof headlineParams, HeadlineRow>(
    DASHBOARD_ID,
    "headline",
    headlineParams,
  );
  const trend = useDashboardChart<typeof trendParams, TrendRow>(
    DASHBOARD_ID,
    "active-trend",
    trendParams,
  );
  const histogram = useDashboardChart<typeof histogramParams, TimeToChurnRow>(
    DASHBOARD_ID,
    "time-to-churn",
    histogramParams,
  );
  const retention = useDashboardChart<typeof retentionParams, RetentionRow>(
    DASHBOARD_ID,
    "yoy-retention",
    retentionParams,
  );
  const availableTiers = useDashboardChart<Record<string, never>, TierRow>(
    DASHBOARD_ID,
    "available-tiers",
    {},
  );

  const headlineRow = headline.data?.[0];
  const tierOptions = useMemo(
    () => ["", ...(availableTiers.data?.map((r) => r.tier) ?? [])],
    [availableTiers.data],
  );

  return (
    <div className="space-y-6">
      <FiltersCard>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Trend fra</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => update({ from: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Trend til</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => update({ to: e.target.value })}
              className="w-44"
            />
          </label>
        </div>
        <div>
          <p className="text-xs text-admin-text-subtle mb-2">Kjønn</p>
          <div className="flex flex-wrap gap-2">
            {GENDERS.map((g) => (
              <Button
                key={g || "all"}
                variant={gender === g ? "default" : "outline"}
                size="sm"
                onClick={() => update({ gender: g })}
              >
                {GENDER_LABELS[g]}
              </Button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs text-admin-text-subtle mb-2">
            Alder ved signup
          </p>
          <div className="flex flex-wrap gap-2">
            {AGE_BUCKETS.map((b) => (
              <Button
                key={b || "all"}
                variant={ageBucket === b ? "default" : "outline"}
                size="sm"
                onClick={() => update({ ageBucket: b })}
              >
                {b === "" ? "Alle aldre" : b}
              </Button>
            ))}
          </div>
        </div>
      </FiltersCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Betalende medlemmer akkurat nå</CardTitle>
          </CardHeader>
          <CardContent>
            {headline.isLoading ? (
              <ChartSkeleton />
            ) : headline.isError ? (
              <ErrorBlock message={headline.error?.message} />
            ) : !headlineRow ? (
              <EmptyBlock>mangler data</EmptyBlock>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                <KpiTile
                  label="Betalende nå"
                  value={headlineRow.active_now}
                  help="Antall brukere med aktivt abonnement. Brukere som har sagt opp, men der abonnementet ennå ikke har utløpt, telles fortsatt som betalende."
                />
                <KpiTile
                  label="Oppsigelser under behandling"
                  value={headlineRow.pending_cancel}
                  help="Brukere som har sagt opp, men der oppsigelsen ikke er bekreftet av Periode enda. Disse vil normalt churne i løpet av neste fakturasyklus."
                />
                <KpiTile
                  label="Reaktiveringer denne måneden"
                  value={headlineRow.reactivations_this_month}
                  help="Brukere som tidligere har churnet og som har et nytt aktivt abonnement startet denne måneden. Telles som betalende, ikke som churn."
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Betalende medlemmer over tid</CardTitle>
            <p className="text-xs text-admin-text-subtle mt-1">
              Antall betalende medlemmer ved slutten av hver måned i perioden.
              En bruker telles som betalende på dato D dersom abonnementet
              startet ≤ D og enten ikke har churnet eller churnet etter D.
            </p>
          </CardHeader>
          <CardContent>
            {trend.isLoading ? (
              <ChartSkeleton />
            ) : trend.isError ? (
              <ErrorBlock message={trend.error?.message} />
            ) : !trend.data || trend.data.length === 0 ? (
              <EmptyBlock>mangler data</EmptyBlock>
            ) : (
              <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart
                    data={trend.data.map((r) => ({
                      day: r.day,
                      active: Number(r.active),
                    }))}
                    margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#2B2B31" />
                    <XAxis dataKey="day" tick={axisStyle} />
                    <YAxis tick={axisStyle} allowDecimals={false} width={48} />
                    <Tooltip content={<TrendTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="active"
                      name="Betalende medlemmer"
                      stroke={LINE_COLOR}
                      strokeWidth={2}
                      dot={{ fill: LINE_COLOR, r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Hvor lenge varer medlemskapet</CardTitle>
            <p className="text-xs text-admin-text-subtle mt-1">
              Fordeling av antall måneder mellom første aktive abonnement og
              siste utløp, for brukere som er klassifisert som churnet i dag.
              Brukere som er betalende nå er ikke med. Abonnementsnivå er det
              brukeren hadde da de churnet — vi lagrer ikke historikk på
              nivåendringer.
            </p>
            {tierOptions.length > 1 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {tierOptions.map((tr) => (
                  <Button
                    key={tr || "all"}
                    variant={tier === tr ? "default" : "outline"}
                    size="sm"
                    onClick={() => update({ tier: tr })}
                  >
                    {tr === "" ? "Alle nivå" : tr}
                  </Button>
                ))}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {histogram.isLoading ? (
              <ChartSkeleton />
            ) : histogram.isError ? (
              <ErrorBlock message={histogram.error?.message} />
            ) : !histogram.data || histogram.data.length === 0 ? (
              <EmptyBlock>mangler data</EmptyBlock>
            ) : (
              <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={histogram.data.map((r) => ({
                      bucket: r.bucket,
                      count: Number(r.count),
                    }))}
                    margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#2B2B31" />
                    <XAxis dataKey="bucket" tick={axisStyle} />
                    <YAxis tick={axisStyle} allowDecimals={false} width={36} />
                    <Tooltip
                      content={<CountTooltip />}
                      cursor={{ fill: "rgba(255,255,255,0.04)" }}
                    />
                    <Bar
                      dataKey="count"
                      name="Brukere"
                      fill={BAR_COLOR}
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Retensjon per signup-måned</CardTitle>
            <p className="text-xs text-admin-text-subtle mt-1">
              Av brukerne som ble aktive en gitt måned, hvor stor andel er
              fortsatt betalende medlemmer i dag. Hver søyle viser én
              signup-måned. Brukere som churnet og kom tilbake telles som
              aktive. En bruker hører til kohorten der de først ble aktive.
            </p>
          </CardHeader>
          <CardContent>
            {retention.isLoading ? (
              <ChartSkeleton />
            ) : retention.isError ? (
              <ErrorBlock message={retention.error?.message} />
            ) : !retention.data || retention.data.length === 0 ? (
              <EmptyBlock>mangler data</EmptyBlock>
            ) : (
              <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart
                    data={retention.data.map((r) => ({
                      signup_month: r.signup_month,
                      cohort_size: Number(r.cohort_size),
                      retained: Number(r.retained),
                      churned: Number(r.churned),
                      retention_pct: Number(r.retention_pct),
                      churn_pct: Number(r.churn_pct),
                    }))}
                    margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#2B2B31" />
                    <XAxis dataKey="signup_month" tick={axisStyle} />
                    <YAxis
                      yAxisId="pct"
                      orientation="left"
                      tick={axisStyle}
                      tickFormatter={(v: number) => `${v}%`}
                      domain={[0, 100]}
                      stroke={BAR_COLOR}
                      width={40}
                    />
                    <YAxis
                      yAxisId="count"
                      orientation="right"
                      tick={axisStyle}
                      allowDecimals={false}
                      stroke={LINE_COLOR}
                      width={40}
                    />
                    <Tooltip
                      content={<RetentionTooltip />}
                      cursor={{ fill: "rgba(255,255,255,0.04)" }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar
                      yAxisId="pct"
                      dataKey="retention_pct"
                      name="Fortsatt aktiv %"
                      fill={BAR_COLOR}
                      radius={[3, 3, 0, 0]}
                    />
                    <Line
                      yAxisId="count"
                      type="monotone"
                      dataKey="cohort_size"
                      name="Signups (antall)"
                      stroke={SIZE_COLOR}
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      dot={{ fill: SIZE_COLOR, r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                    <Line
                      yAxisId="count"
                      type="monotone"
                      dataKey="retained"
                      name="Fortsatt aktive (antall)"
                      stroke={LINE_COLOR}
                      strokeWidth={2}
                      dot={{ fill: LINE_COLOR, r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiTile({
  label,
  value,
  help,
}: {
  label: string;
  value: number | string;
  help: string;
}) {
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
      <p className="text-xs text-admin-text-subtle">{label}</p>
      <p className="text-3xl font-semibold tabular-nums text-admin-text-primary mt-1">
        {Number(value).toLocaleString("no-NO")}
      </p>
      <p className="text-xs text-admin-text-subtle mt-2 leading-snug">{help}</p>
    </div>
  );
}

function TrendTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-admin-text-primary mb-1">
        {label}
      </p>
      <p className="text-xs" style={{ color: LINE_COLOR }}>
        {Number(payload[0]?.value).toLocaleString("no-NO")} betalende medlemmer
      </p>
    </div>
  );
}

function CountTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-admin-text-primary mb-1">
        {label}
      </p>
      <p className="text-xs" style={{ color: BAR_COLOR }}>
        {Number(payload[0]?.value).toLocaleString("no-NO")} brukere
      </p>
    </div>
  );
}

function RetentionTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const r = payload[0]?.payload as
    | {
        signup_month: string;
        cohort_size: number;
        retained: number;
        churned: number;
        retention_pct: number;
        churn_pct: number;
      }
    | undefined;
  if (!r) return null;
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-admin-text-primary mb-1">
        Signup-måned: {label}
      </p>
      <p className="text-xs text-admin-text-secondary">
        {r.cohort_size} signups
      </p>
      <p className="text-xs text-admin-text-secondary">
        {r.retained} fortsatt betalende i dag
      </p>
      <p className="text-xs" style={{ color: BAR_COLOR }}>
        {r.retention_pct}% retensjon
      </p>
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="flex items-center gap-2 text-admin-text-subtle text-sm py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" />
      Laster…
    </div>
  );
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm text-admin-text-subtle py-6 text-center">
      {children}
    </p>
  );
}

function ErrorBlock({ message }: { message?: string }) {
  return (
    <div className="rounded-md border border-admin-danger-500/30 bg-admin-danger-500/5 p-4 text-sm text-admin-danger-400">
      {message || "Ukjent feil"}
    </div>
  );
}
